import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AsamcoUserService } from '@asamco/base';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Company } from '../interfaces/company.interface';
import { DashboardItem } from '../interfaces/dashboard-item.interface';
import { functionAppLink } from '../interfaces/function-app-link.interface';
import { functionAppsDetailed } from '../interfaces/functionApps-detailed.interface';
import { functionApps, functionAppsCreateDto } from '../interfaces/functionApps.interface';
import { Link } from '../interfaces/link.interface';
import { SelectedCompanyService } from '../selected-company/selected-company.service';
import { DashboardItemService } from '../services/dashboard-item.service';
import { FunctionAppsLinkService } from '../services/function-apps-link.service';
import { FunctionAppsService } from '../services/function-apps.service';
import { LinkTemplateService } from '../services/link-template.service';
import { LinkService } from '../services/link.service';
import { Application } from '../interfaces/application.interface';
import { ApplicationService } from '../services/application.service';
import { LinkTemplate } from '../interfaces/link-template.interface';
import { showErrorSwal } from '@asamco/utils';
import Swal from 'sweetalert2';
import { AsyncPipe, NgIf } from '@angular/common';
import { DetailedGridLinkFunctionappsComponent } from './detailed-grid-link-functionapps/detailed-grid-link-functionapps.component';
import { DxiColumnModule, DxoMasterDetailModule, DxoSelectionModule } from 'devextreme-angular/ui/nested';
import { DxDataGridModule, DxSelectBoxModule } from 'devextreme-angular';
import { DetailedGridComponent } from './detailed-grid/detailed-grid.component';

@Component({
	selector: 'app-shell-function-apps',
	templateUrl: './function-apps.component.html',
	styleUrls: ['./function-apps.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, AsyncPipe, DetailedGridLinkFunctionappsComponent, DxoMasterDetailModule, DxiColumnModule, DxoSelectionModule, DxDataGridModule, DxSelectBoxModule, DetailedGridComponent]
})
export class FunctionAppsComponent implements OnInit {
	public isLoadingFunctionAppsInfo: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public isAdminSubject = new BehaviorSubject<boolean>(false);
	public linksConfiguration$: Observable<any[]> = new Observable<any[]>();
	public functionApps$ = new BehaviorSubject<functionAppsDetailed[]>([]);
	public companies$ = new BehaviorSubject<Company[]>([]);
	public applications$ = new BehaviorSubject<Application[]>([]);
	public linkTemplates$ = new BehaviorSubject<LinkTemplate[]>([]);

	constructor(
		private asamcoUserService: AsamcoUserService,
		private linkTemplateService: LinkTemplateService,
		private functionAppsService: FunctionAppsService,
		private linkService: LinkService,
		private applicationService: ApplicationService,
		private selectedCompanyService: SelectedCompanyService
	) {}

	ngOnInit(): void {
		this.asamcoUserService.isAdmin().then(result => {
			this.isAdminSubject.next(result);
			this.getRequiredData();
			this.getFunctionAppItems();
		});
	}

	getRequiredData() {
		forkJoin({
			applications: this.applicationService.get(),
			linkTemplates: this.linkTemplateService.getAllLinkTemplates(),
			companies: this.selectedCompanyService.getCompanies(),
		}).subscribe(({ applications, linkTemplates, companies }) => {
			this.applications$.next(applications);
			this.linkTemplates$.next(linkTemplates);
			this.companies$.next(companies);
		});
	}

	selectCompany(event: any) {
		//console.log(event)
		const companyId = event.value;

		const linkServiceCall = this.linkService.getByCompanyId(companyId);

		// Combine the results using forkJoin and map operator
		this.linksConfiguration$ = linkServiceCall;
	}

	getFunctionAppItems() {
		this.isLoadingFunctionAppsInfo.next(true);
		this.functionAppsService.getfunctionAppsDetailed().subscribe(e => {
			this.functionApps$.next(e);
			this.isLoadingFunctionAppsInfo.next(false);
		});
	}

	rowInserted(data: any) {
		console.log(data);
		//Create new entry
		const newFunctionApp: functionAppsCreateDto = {
			name: data.data.functionApps.name,
			description: data.data.functionApps.description,
			applicationId: data.data.functionApps.applicationId,
			url: data.data.functionApps.url,
			isDefault: data.data.functionApps.isDefault ? data.data.functionApps.isDefault : false,
			masterKey: data.data.functionApps.masterKey,
			systemKey: data.data.functionApps.systemKey,
		};

		this.functionAppsService.createFunctionApps(newFunctionApp).subscribe(
			e => {
				Swal.fire({
					icon: 'success',
					title: 'Link function app configuration saved',
					showConfirmButton: false,
					timer: 1500,
				});
				this.getFunctionAppItems();
			},
			error => {
				showErrorSwal(error);
			}
		);
	}

	rowUpdating(data: any) {
		data.cancel = true;
		console.log(data);

		const updatedEntity = this.updateModel(data.oldData.functionApps, data.newData.functionApps);
		this.functionAppsService.updateFunctionApps(updatedEntity).subscribe(
			e => {
				Swal.fire({
					icon: 'success',
					title: 'Link function app configuration saved',
					showConfirmButton: false,
					timer: 1500,
				});
				this.getFunctionAppItems();
			},
			error => {
				showErrorSwal(error);
			}
		);
	}

	updateModel(oldModel: functionApps, updatedFields: Partial<functionApps>): functionApps {
		// Create a new object to avoid modifying the original oldModel
		const newModel: functionApps = { ...oldModel, ...updatedFields };
		return newModel;
	}

	rowRemoving(data: any) {
		//Cancel event, otherwise the row gets visually deleted anyway...
		data.cancel = true;
		Swal.fire({
			title: `Are you sure you want to delete '${data.data.functionApps.name}'?`,
			text: 'This also deletes all custom function app link configurations with this function app, this cannot be undone!',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#3085d6',
			cancelButtonColor: '#d33',
			confirmButtonText: 'Yes, delete it!',
		}).then(result => {
			if (result.isConfirmed) {
				this.functionAppsService.deleteFunctionApps(data.data.functionApps.id).subscribe(
					e => {
						Swal.fire({
							icon: 'success',
							title: 'Function app entry deleted',
							showConfirmButton: false,
							timer: 1500,
						});
						//reload data'
						this.getRequiredData();
						this.getFunctionAppItems();
					},
					error => {
						showErrorSwal(error);
					}
				);
			}
		});
	}
}
